import React, { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { GetItemsFilters } from '../types'; // フィルターの型定義をインポート
import { parseNumberOrEmpty } from '../utils/parsing';

// 1. Contextが提供する値の型を定義
interface ItemFilterContextType {
  itemFilters: GetItemsFilters;
  // URLと同期する便利な関数を追加（オプション）
  updateFiltersAndNavigate: (newFilters: GetItemsFilters) => void;
}

// 2. Contextオブジェクトを作成 (デフォルトはundefined)
const ItemFilterContext = createContext<ItemFilterContextType | undefined>(undefined);

// 3. Providerコンポーネントを作成
interface ItemFilterProviderProps {
  children: ReactNode;
}

export const ItemFilterProvider: React.FC<ItemFilterProviderProps> = ({ children }) => {
  // SearchParamsのセッターの利用を宣言
  const [searchParams, setSearchParams] = useSearchParams();

  // URL(searchParams)が変わるたびに、itemFiltersオブジェクトを再計算する
  const itemFilters: GetItemsFilters = useMemo(() => {

    const filtersFromUrl: GetItemsFilters = {
        // 日付 (string | '')
        receptionDate: searchParams.get('receptionDate') || '',
        receptionDateFrom: searchParams.get('receptionDateFrom') || '',
        receptionDateTo: searchParams.get('receptionDateTo') || '',
        biddingDate: searchParams.get('biddingDate') || '',
        biddingDateFrom: searchParams.get('biddingDateFrom') || '',
        biddingDateTo: searchParams.get('biddingDateTo') || '',
        // 文字列 (string | '' or string | undefined)
        brand: searchParams.get('brand') || '', // '' をデフォルトに
        // 数値 (number | '')
        teaTypeCode: parseNumberOrEmpty(searchParams.get('teaTypeCode')),
        varietyCode: parseNumberOrEmpty(searchParams.get('varietyCode')),
        teaSeasonCode: parseNumberOrEmpty(searchParams.get('teaSeasonCode')),
        listingCategory: parseNumberOrEmpty(searchParams.get('listingCategory')),
        prefectureType: parseNumberOrEmpty(searchParams.get('prefectureType')),
        status: parseNumberOrEmpty(searchParams.get('status')),
        sellerCode: parseNumberOrEmpty(searchParams.get('sellerCode')),
        farmerCode: parseNumberOrEmpty(searchParams.get('farmerCode')),
        gapCode: parseNumberOrEmpty(searchParams.get('gapCode')),
        caltivationCategory: parseNumberOrEmpty(searchParams.get('caltivationCategory')),
        productionHistory: parseNumberOrEmpty(searchParams.get('productionHistory')),

        sortKeys: searchParams.getAll('sortKeys'),

        biddingDisplayCondition : parseNumberOrEmpty(searchParams.get('biddingDisplayCondition')),
    };
    return filtersFromUrl;

  }, [searchParams]); // searchParamsが変わるたびに再計算

  // // ステートitemFiltersとそのセッターの定義
  // // ※ステートitemFiltersには初期値としてinitialFilterStateをセットする
  // const [itemFilters, setItemFilters] = useState<GetItemsFilters>(initialFilterState);

  // フィルターを更新し、URLのクエリパラメータも同時に更新するヘルパー関数を定義
  // ローカルstateをもとにURLパラメータも同内容で更新する
  const updateFiltersAndNavigate = (newFilters: GetItemsFilters) => {
    // 1. 引数の内容でURLSearchParamsを更新
    const newParams = new URLSearchParams();
    (Object.keys(newFilters) as Array<keyof GetItemsFilters>).forEach(key => {
      const value = newFilters[key];
      if (value || value === 0) { // 値が存在するものだけをURLに追加
        if (Array.isArray(value)) {
          // 配列の中身をループして append する
          // 結果: ?sortKeys=Value1&sortKeys=Value2... となる
          value.forEach(v => {
            if (v) newParams.append(key, String(v));
          });
        } else {
          // 通常の単一値の場合
          newParams.set(key, String(value));
        }
      }
    });
    setSearchParams(newParams);
  };

  // valueプロパティを定義（ステートと関数をセット）
  // ※useMemoを使うことでvalueオブジェクトが不必要に再生成されないようにする
  // ※useMemoを使わないとItemFilterProviderコンポーネントが再レンダリングのたびにvalueの中身が変わってしまい、
  // 　上位でvalueプロパティをトリガーにしているものが無駄に動いてしまう
  const value = useMemo(() => ({
    itemFilters,
    updateFiltersAndNavigate // ContextとURLを同時に更新する関数
  }), [itemFilters, setSearchParams]);

  return (
    <ItemFilterContext.Provider value={value}>
      {children}
    </ItemFilterContext.Provider>
  );
};

// 5. Contextを使用するためのカスタムフック
export const useItemFilters = () => {
  const context = useContext(ItemFilterContext);
  if (context === undefined) {
    // Providerコンポーネント内で使われていない場合にエラーを出す
    throw new Error('useItemFilters must be used within a ItemFilterProvider');
  }
  return context;
};